import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { bookingApi } from '../../utils/bookingApi';
import { useFeature } from '../../hooks/useFeature';
import { PageHeader, Skeleton, EmptyState, ErrorState, useConfirm, useToast } from '../../components/ui';
import './booking-shared.css';
import { sessionWhen, whenExpires } from './bookingDates';

function isToday(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  return d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const COMMITMENT_STATUS = {
  ACTIVE: { label: 'Active', tone: 'good' },
  PAUSED: { label: 'Paused', tone: 'off' },
  WAITLISTED: { label: 'Waitlisted', tone: 'wait' },
};

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [leaving, setLeaving] = useState(null);
  const waitlistsOn = useFeature('waitlists');
  const confirm = useConfirm();
  const toast = useToast();

  const load = useCallback(() => {
    setFailed(false);
    Promise.all([
      bookingApi.getMyBookings(),
      bookingApi.getMyWaitlist().catch(() => ({ data: [] })),
      bookingApi.getMyCommitments().catch(() => ({ data: [] })),
    ]).then(([bRes, wRes, cRes]) => {
      setBookings(bRes.data.filter(b => b.status === 'CONFIRMED'));
      setWaitlist(wRes.data);
      setCommitments(cRes.data);
    }).catch(() => {
      setFailed(true);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = () => { setLoading(true); load(); };

  const handleCancel = async (bookingId, sessionDate) => {
    const dayOf = isToday(sessionDate);
    const ok = await confirm({
      title: 'Cancel this booking?',
      message: dayOf
        ? 'Same-day cancellations do not receive a credit refund.'
        : 'A credit will be issued for each gymnast.',
      confirmLabel: 'Cancel booking',
      cancelLabel: 'Keep booking',
    });
    if (!ok) return;
    setCancelling(bookingId);
    setCancelError(null);
    try {
      await bookingApi.cancelBooking(bookingId);
      toast('Booking cancelled');
      load();
    } catch (err) {
      setCancelError(err.response?.data?.error || "Couldn't cancel the booking. Please try again.");
    } finally {
      setCancelling(null);
    }
  };

  const handleCancelLine = async (bookingId, line, sessionDate) => {
    const dayOf = isToday(sessionDate);
    const name = `${line.gymnast.firstName} ${line.gymnast.lastName}`;
    const ok = await confirm({
      title: `Cancel ${name} from this session?`,
      message: dayOf
        ? 'Same-day cancellations do not receive a credit refund.'
        : 'A credit will be issued.',
      confirmLabel: 'Cancel booking',
      cancelLabel: 'Keep booking',
    });
    if (!ok) return;
    const key = `${bookingId}:${line.id}`;
    setCancelling(key);
    setCancelError(null);
    try {
      await bookingApi.cancelBookingLine(bookingId, line.id);
      toast(`${line.gymnast.firstName}'s booking cancelled`);
      load();
    } catch (err) {
      setCancelError(err.response?.data?.error || "Couldn't cancel. Please try again.");
    } finally {
      setCancelling(null);
    }
  };

  // Leaving gives up the place in the queue, so it asks first like a cancel
  const handleLeave = async (instanceId) => {
    const ok = await confirm({
      title: 'Leave this waitlist?',
      message: "You'll lose your place in the queue for this session.",
      confirmLabel: 'Leave',
      cancelLabel: 'Stay on it',
    });
    if (!ok) return;
    setLeaving(instanceId);
    setCancelError(null);
    try {
      await bookingApi.leaveWaitlist(instanceId);
      toast('Left the waitlist');
      load();
    } catch (err) {
      setCancelError(err.response?.data?.error || "Couldn't leave the waitlist. Please try again.");
    } finally {
      setLeaving(null);
    }
  };

  const waiting = waitlistsOn ? waitlist.filter(e => e.status !== 'OFFERED') : [];
  const header = <PageHeader title="My bookings" />;

  if (loading) return <div className="bk-page bk-page--narrow">{header}<Skeleton rows={3} label="Loading bookings…" /></div>;
  if (failed) return <div className="bk-page bk-page--narrow">{header}<ErrorState title="Couldn't load your bookings" onRetry={retry} /></div>;

  return (
    <div className="bk-page bk-page--narrow">
      {header}
      {cancelError && <p className="bk-error" role="alert">{cancelError}</p>}

      {waitlist.filter(e => e.status === 'OFFERED').map(e => (
        <div key={e.id} className="bk-card" style={{ borderColor: 'var(--booking-accent)', borderWidth: 2 }}>
          <p style={{ fontWeight: 700, color: 'var(--booking-accent)', margin: '0 0 0.25rem' }}>
            A place opened up for you
          </p>
          <p style={{ margin: '0 0 0.15rem' }}>{sessionWhen(e.sessionInstance.date, e.sessionInstance.template)}</p>
          {e.offerExpiresAt && (
            <p className="bk-small" style={{ margin: 0 }}>Book it by {whenExpires(e.offerExpiresAt)}.</p>
          )}
          <div className="bk-row-actions">
            <Link to={`/booking/session/${e.sessionInstanceId}`} className="bk-btn bk-btn--primary bk-btn--sm">
              Book this place
            </Link>
          </div>
        </div>
      ))}

      {commitments.length > 0 && (
        <>
          <h2 className="bk-section-title" style={{ marginTop: '1.25rem' }}>Standing slots</h2>
          {commitments.map(c => {
            const status = COMMITMENT_STATUS[c.status] || { label: c.status, tone: 'off' };
            return (
              <div key={c.id} className="bk-card" style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'baseline' }}>
                <div>
                  <strong>{DAYS[c.template.dayOfWeek]}s · {c.template.startTime} to {c.template.endTime}</strong>
                  <div className="bk-muted" style={{ fontSize: 'var(--text-body)' }}>{c.gymnast.firstName} {c.gymnast.lastName}</div>
                </div>
                <span className={`bk-pill bk-pill--${status.tone}`}>{status.label}</span>
              </div>
            );
          })}
          <h2 className="bk-section-title" style={{ marginTop: '1.25rem' }}>Upcoming bookings</h2>
        </>
      )}

      {bookings.length === 0 && (
        <EmptyState
          icon={CalendarDaysIcon}
          title="No upcoming bookings"
          text="Book a session from the calendar and it shows here."
          action={<Link to="/booking" className="bk-btn bk-btn--primary">Book a session</Link>}
        />
      )}
      {bookings.map(b => {
        const dayOf = isToday(b.sessionInstance.date);
        const activeLines = (b.lines || []).filter(l => !l.cancelledAt);
        const single = activeLines.length === 1;
        return (
          <div key={b.id} className="bk-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'baseline', marginBottom: '0.35rem' }}>
              <strong>{sessionWhen(b.sessionInstance.date, b.sessionInstance.template)}</strong>
              <span className="bk-muted" style={{ whiteSpace: 'nowrap' }}>£{(b.totalAmount / 100).toFixed(2)}</span>
            </div>
            {activeLines.map(line => {
              // With one gymnast, cancelling them cancels the booking
              const key = single ? b.id : `${b.id}:${line.id}`;
              return (
                <div key={line.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', borderTop: '1px solid var(--booking-border)' }}>
                  <span>{line.gymnast.firstName} {line.gymnast.lastName}</span>
                  <button
                    type="button"
                    onClick={() => single
                      ? handleCancel(b.id, b.sessionInstance.date)
                      : handleCancelLine(b.id, line, b.sessionInstance.date)}
                    disabled={cancelling === key}
                    className="bk-btn bk-btn--sm bk-btn--outline-danger"
                  >
                    {cancelling === key ? 'Cancelling…' : 'Cancel'}
                  </button>
                </div>
              );
            })}
            {dayOf && (
              <p className="bk-small" style={{ margin: '0.35rem 0 0' }}>
                Cancelling on the day doesn't give a credit.
              </p>
            )}
          </div>
        );
      })}

      {waiting.length > 0 && (
        <section id="waitlist">
          <h2 className="bk-section-title" style={{ marginTop: '1.25rem' }}>Waitlists</h2>
          {waiting.map(e => (
            <div key={e.id} className="bk-card" style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
              <div>
                <strong>{sessionWhen(e.sessionInstance.date, e.sessionInstance.template)}</strong>
                <div className="bk-small">You're on the waitlist. We'll let you know if a place comes up.</div>
              </div>
              <button
                type="button"
                className="bk-btn bk-btn--outline-danger bk-btn--sm"
                onClick={() => handleLeave(e.sessionInstanceId)}
                disabled={leaving === e.sessionInstanceId}
              >
                {leaving === e.sessionInstanceId ? 'Leaving…' : 'Leave'}
              </button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
